import React from 'react';
import Modal from 'react-modal';
import { useHistory, withRouter } from 'react-router-dom';
import { useQuery } from 'react-apollo';

import Button from '../../../global/Button';
import Spinner from '../../../global/Spinner';
import { isVGBContext } from '../../../../util/album';
import useBasename from '../../../routing/useBasename';
import useOpenModal from '../../../../hooks/useOpenModal';
import {
  ALBUM_SLIDESHOW_STATUS,
  ALBUM_SUBTYPE,
} from '../../../../constants/albums';
import { CONTRIBUTOR_SHARE_MODAL } from '../../../../constants/modals';
import {
  AlbumWithEventSettings,
  AlbumWithGreetingVideo,
} from '../../../../graphql/queries/albums';
import ContributorShareModal from '../../../../apps/pro/components/ContributorShare/ContributorShare';
import { getCelebratedPersonName } from '../../../../apps/pro/views/AlbumFolder/FreemiumEvents/SpecialtyAlbumGreetingModal/helpers';
import FullScreenLoader from '../../../global/FullScreenLoader';
import useAuthenticated from '../../../../hooks/useAuthenticated';
import { MeQuery } from '../../../../graphql/queries/account';
import { isAlbumOwner } from '../../../../util/account';
import Angle from '../../../global/Angle';
import {
  isNativeAppWebView,
  postNativeAppAlbumSlideshowViewerDismiss,
  postNativeAppAlbumSlideshowViewerShareDialog,
  postNativeAppAlbumSlideshowViewerUpload,
} from '../../../../services/native-bridge/native-apps-interface';

const VideoSlideshowSolicitation = ({ match }) => {
  const openModal = useOpenModal();
  const basename = useBasename();
  const history = useHistory();
  const authenticated = useAuthenticated();
  const { data: { me: user } = {} } = useQuery(MeQuery, {
    skip: !authenticated,
  });
  const { data: { album, loading } = {} } = useQuery(AlbumWithEventSettings, {
    variables: {
      id: match.params.albumId,
    },
    skip: !match?.params?.albumId,
  });
  const { data: { album: albumForGreeting } = {} } = useQuery(
    AlbumWithGreetingVideo,
    {
      variables: {
        id: match.params.albumId,
      },
      skip: !match?.params?.albumId,
    },
  );

  const isOwner = isAlbumOwner(user, album);
  const isNativeView = isNativeAppWebView();
  const eventSettings = album?.root_album_folder?.event_settings;
  const isPublished =
    album?.slideshow?.status === ALBUM_SLIDESHOW_STATUS.PUBLISHED;
  const posessiveCelebratedName = getCelebratedPersonName(
    album?.album_subtype,
    eventSettings,
    true,
    album?.root_album_folder?.account?.organization_name ??
      album?.root_album_folder?.account?.name,
  );

  const hasUploadedGreetingVideo = !!albumForGreeting?.greeting_video
    ?.records?.[0];

  const solicitationCopy = (() => {
    if (!album) {
      return '';
    }

    if (isOwner && isVGBContext(album)) {
      return (
        <>
          Share your Video Guestbook <br />
          &amp; let&apos;s collect some memories!
        </>
      );
    }

    if (isOwner) {
      return (
        <>
          Share your Group Video <br />
          &amp; let&apos;s collect some memories!
        </>
      );
    }

    if (isVGBContext(album)) {
      return album.album_subtype === ALBUM_SUBTYPE.MEMORIALS ? (
        <>
          Add your message to {posessiveCelebratedName} family Video Guestbook
        </>
      ) : (
        <>Add your well wishes to {posessiveCelebratedName} Video Guestbook!</>
      );
    }

    return <>Add your message to {posessiveCelebratedName} Group Video!</>;
  })();

  const addVideoCopy = (() => {
    if (hasUploadedGreetingVideo && isOwner && isVGBContext(album)) {
      return 'Edit greeting';
    }

    if (isOwner && isVGBContext(album)) {
      return 'Add greeting';
    }

    if (hasUploadedGreetingVideo && isOwner) {
      return 'Edit custom prompt';
    }

    if (isOwner) {
      return 'Add custom prompt';
    }

    return 'Add a message';
  })();

  const shareCopy = (() => {
    if (!isPublished && !isVGBContext(album)) {
      return 'Request videos from others';
    }

    if (!isVGBContext(album)) {
      return 'Invite others to view';
    }

    return 'Share with others';
  })();

  const watchAgainPath = (() => {
    if (!isPublished && !isOwner) {
      basename
        ? `/app/${basename}/albums/${album?.id}/slideshow/greeting`
        : `/albums/${album?.id}/slideshow/greeting`;
    }

    return basename
      ? `/app/${basename}/albums/${album?.id}/slideshow`
      : `/albums/${album?.id}/slideshow`;
  })();

  if (!match?.params?.albumId || !album) {
    return <FullScreenLoader />;
  }

  return (
    <Modal
      isOpen
      ariaHideApp={false}
      bodyOpenClassName="body-modal-open"
      overlayClassName="slideshow-grid-viewer-overlay"
      className="slideshow-grid-viewer"
    >
      <Button
        btnStyle="reset"
        onClick={() => {
          if (isNativeView) {
            postNativeAppAlbumSlideshowViewerDismiss();
          } else {
            history.goBack();
          }
        }}
        className="video-slideshow-btn-dismiss text-lg"
      >
        <Angle direction="left" />
      </Button>
      <div className="slideshow-grid-viewer-body">
        <div className="slideshow-grid-viewer-content solicitation-view">
          {(!album || loading) && <Spinner />}
          {album && (
            <div className="video-slideshow-solicitation-box">
              <p className="text-white text-center text-bold">
                {solicitationCopy}
              </p>
              {(!isOwner ||
                isVGBContext(album) ||
                !hasUploadedGreetingVideo) && (
                <Button
                  btnBlock
                  btnOutline
                  btnStyle="white"
                  onClick={() => {
                    if (isNativeView) {
                      postNativeAppAlbumSlideshowViewerUpload(album?.id);
                    } else {
                      history.push(
                        `/s/event-invite/${album?.root_album_folder.id}?target=${album?.id}`,
                      );
                    }
                  }}
                >
                  {addVideoCopy}
                </Button>
              )}
              <Button
                btnBlock
                btnOutline
                btnStyle="white"
                onClick={() => {
                  if (isNativeView) {
                    postNativeAppAlbumSlideshowViewerShareDialog(album?.id);
                  } else {
                    openModal(CONTRIBUTOR_SHARE_MODAL, {
                      albumId: album?.id,
                    });
                  }
                }}
              >
                {shareCopy}
              </Button>
              <Button
                btnBlock
                btnOutline
                btnStyle="white"
                onClick={() => {
                  history.replace(watchAgainPath);
                }}
              >
                Watch again
              </Button>
            </div>
          )}
        </div>
      </div>
      <ContributorShareModal />
    </Modal>
  );
};

export default withRouter(VideoSlideshowSolicitation);
